import React, { useState } from "react";
import { Box, Typography, Grid, Paper, MenuItem, Select, Card, CardContent } from "@mui/material";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export const Home = () => {
  const [filter, setFilter] = useState("monthly"); // Filter for monthly/yearly view

  // Dummy data for the graphs
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const countries = ["India", "Singapore", "UAE", "Australia"];

  const customerOnboardedData = {
    labels: months,
    datasets: [
      {
        label: "India",
        data: [50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160],
        backgroundColor: "#4caf50",
      },
      {
        label: "Singapore",
        data: [40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150],
        backgroundColor: "#2196f3",
      },
      {
        label: "UAE",
        data: [30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140],
        backgroundColor: "#ff9800",
      },
      {
        label: "Australia",
        data: [20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130],
        backgroundColor: "#9c27b0",
      },
    ],
  };

  const customerComplaintsData = {
    labels: months,
    datasets: [
      {
        label: "Complaints",
        data: [20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75],
        borderColor: "#f44336",
        backgroundColor: "rgba(244, 67, 54, 0.2)",
        tension: 0.4,
      },
      {
        label: "Resolved",
        data: [15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70],
        borderColor: "#4caf50",
        backgroundColor: "rgba(76, 175, 80, 0.2)",
        tension: 0.4,
      },
    ],
  };

  const salesRevenueData = {
    labels: months,
    datasets: [
      {
        label: "Sales Revenue",
        data: [12000, 15000, 18000, 20000, 22000, 25000, 27000, 30000, 32000, 35000, 37000, 40000],
        borderColor: "#2196f3",
        backgroundColor: "rgba(33, 150, 243, 0.2)",
        tension: 0.4,
      },
    ],
  };

  const propertyClosedData = {
    labels: months,
    datasets: [
      {
        label: "Closed Deals",
        data: [10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65],
        borderColor: "#ff9800",
        backgroundColor: "rgba(255, 152, 0, 0.2)",
        tension: 0.4,
      },
    ],
  };

  const profitData = {
    labels: months,
    datasets: [
      {
        label: "Profit",
        data: [5000, 7000, 9000, 11000, 13000, 15000, 17000, 19000, 21000, 23000, 25000, 27000],
        borderColor: "#4caf50",
        backgroundColor: "rgba(76, 175, 80, 0.2)",
        tension: 0.4,
      },
    ],
  };

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  return (
    <Box sx={{ padding: "24px" }}>
      {/* Page Title */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: "bold",
          marginBottom: "16px",
          textAlign: "center",
        }}
      >
        Dashboard Overview
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ marginBottom: "24px" }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Total Revenue
              </Typography>
              <Typography variant="h4" sx={{ color: "#2196f3", fontWeight: "bold" }}>
                $1,200,000
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Total Customers
              </Typography>
              <Typography variant="h4" sx={{ color: "#4caf50", fontWeight: "bold" }}>
                5,000
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Complaints
              </Typography>
              <Typography variant="h4" sx={{ color: "#f44336", fontWeight: "bold" }}>
                1,200
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Closed Deals
              </Typography>
              <Typography variant="h4" sx={{ color: "#ff9800", fontWeight: "bold" }}>
                800
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter Section */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
        <Select
          value={filter}
          onChange={handleFilterChange}
          sx={{ minWidth: "150px" }}
        >
          <MenuItem value="monthly">Monthly View</MenuItem>
          <MenuItem value="yearly">Yearly View</MenuItem>
        </Select>
      </Box>

      {/* Graphs Section */}
      <Grid container spacing={3}>
        {/* Customer Onboarded */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: "16px" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
              Customer Onboarded by Country
            </Typography>
            <Bar data={customerOnboardedData} options={{ responsive: true }} />
          </Paper>
        </Grid>

        {/* Customer Complaints */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: "16px" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
              Customer Complaints and Resolved
            </Typography>
            <Line data={customerComplaintsData} options={{ responsive: true }} />
          </Paper>
        </Grid>

        {/* Sales Revenue */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: "16px" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
              Sales Revenue
            </Typography>
            <Line data={salesRevenueData} options={{ responsive: true }} />
          </Paper>
        </Grid>

        {/* Property Closed Deals */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: "16px" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
              Property Closed Deals
            </Typography>
            <Line data={propertyClosedData} options={{ responsive: true }} />
          </Paper>
        </Grid>

        {/* Profit */}
        {/* <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: "16px" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
              Profit by Month
            </Typography>
            <Line data={profitData} options={{ responsive: true }} />
          </Paper>
        </Grid> */}
      </Grid>
    </Box>
  );
};

export default Home;